import OverStore from "data/OverStore";
import Dispatcher from "Dispatcher";
import ActiveFormActions from "data/ActiveFormActions";
import { FIRST_FIELD } from "data/ActiveFormStore";
import { __range__ } from "lib/decaf";

export const FOCUS_TRACKABLE_SELECTOR = ".control, .keyboard-focusable";

/**
 * I record a focus movement request w/in a container, based on the current
 * element. If no current element is specified (i.e., is `null`), focus will be
 * moved to the first focusable element.
 * @param fluxContext the flux context focus is moving within
 * @param $container jQuery instance to move focus within (a record context).
 * @param curr DOM node that currently has focus to move relative to.
 * @param forward boolean whether to move forward (the default) or not.
 */
const moveFocus = (fluxContext, $container, curr, forward = true) => {
    if (curr) {
        if (curr instanceof jQuery) {
            curr = curr[0]; // unwrap
        }
        if (!curr.id) {
            if (process.env.NODE_ENV !== "production") {
                throw new Error("Focus anchor with no ID!");
            }
            return; // can't do anything...
        }
        Dispatcher.contextDispatch(fluxContext, {
            type: ActiveFormActions.MOVE_FOCUS,
            recordId: $container.data("id"),
            fieldKey: curr.dataset.shortname, // null for non-controls
            domId: curr.id,
            delta: forward ? 1 : -1,
        });
    } else {
        Dispatcher.contextDispatch(fluxContext, {
            type: ActiveFormActions.PLACE_FOCUS_AT_START,
            recordId: $container.data("id"),
        });
    }
};

export const makeRecordSelector = tuple =>
    // don't do this, to allow maintaining focus across a version change
    //   [data-entity-key='${tuple.entityKey}']
    `.entity-context[data-id=${tuple.recordId}]`;

export const makeControlSelector = tuple =>
    `.control-container-${tuple.fieldKey}`;

const focusMovementHelper = ($container, curr, delta) => {
    if(OverStore.isModalInteractive()) {
        return; // prevent any lingering focus changes when modal is visible and interactive.
    }
    const $form_items = $container.find(FOCUS_TRACKABLE_SELECTOR);
    let index = -1; // before the start
    if (curr != null) {
        index = $form_items.index(curr);
        if (delta === 0) {
            placeFocus(curr);
            return; // don't need to move, just place
        }
    }
    if (delta > 0 && (index + delta >= $form_items.length)) {
        placeFocus(curr);
        return; // already at the end
    }
    if (delta < 0 && (index === 0)) {
        placeFocus(curr);
        return; // already at the beginning
    }
    for (const i of Array.from((delta > 0 ? __range__(index + delta, $form_items.length, false) : __range__(index + delta, 0, true)))) {
        if (placeFocus($form_items.get(i))) {
            return;
        }
    }
    // well hell
    placeFocus(curr);
};

export const doFocusMovement = ($form, fm) => {
    const recSel = makeRecordSelector(fm);
    const $container = $form.is(recSel)
        ? $form
        : $form.find(recSel);
    if ($container.length === 0) {
        return;
    }
    if (fm.domId) {
        const byId = document.getElementById(fm.domId);
        if (byId && $container[0].contains(byId)) {
            focusMovementHelper($container, byId, fm.delta);
            return; // woo!
        }
    }
    if (fm.fieldKey) {
        if (fm.fieldKey === FIRST_FIELD) {
            focusMovementHelper($container, null, 1);
            return;
        }
        const $byKey = $container.find(makeControlSelector(fm));
        if ($byKey.length) {
            let delta = fm.delta;
            // orNextEnabled will try to land focus on the exact field or if
            // it is disabled will moved to the next enabled field.
            if(fm.orNextEnabled && delta === 0) {
                const $focusable = getFocusable($byKey);
                if($focusable != null && !isEnabled($focusable)) {
                    delta = 1;
                }
            }
            focusMovementHelper($byKey.parents(recSel).first(), $byKey, delta);
        }
    }
    // console.warn("doFocusMovement missed:", it);
};

const CONTROL_TYPE = {
  REACT: "react",
  OTHER: "dom"
};

export function placeFocus(it) {
    const $it = $(it);
    try {
        const $focusable = getFocusable($it);
        if($focusable != null && isEnabled($focusable)) {
            $focusable.focus();
            return true;
        }
    } catch (e) {
        console.error("placeFocus errored:", e.message, it, e);
    }
    // console.warn("placeFocus missed:", it);
    return false;
}

const isEnabled = ($it) =>
    ($it.is(":enabled") || ! $it.is("[disabled]"));

const getFocusable = ($it) => {
    if (getControlType($it) === CONTROL_TYPE.REACT) {
        // The controls use .focusable, but buttons and other non-controls
        // use .keyboard-focusable. We want the control to win, but if there
        // isn't one, look for a button or whatever.
        for (const sel of [".focusable", FOCUS_TRACKABLE_SELECTOR]) {
            const $focusable = $it.find(sel);
            if($focusable.length > 0) {
                return $focusable.first();
            }
        }
        return null;
    }
    return $it;
};

const getControlType = ($it) => {
    if ($it.is(".react-control")) return CONTROL_TYPE.REACT;
    if ($it.is(".control")) {
        console.log("non-React control:", $it[0]);
        throw new Error("Found a non-React control?!");
    }
    return CONTROL_TYPE.OTHER;
};

export default moveFocus;



// WEBPACK FOOTER //
// ./app/lib/dataentry/lib/dataentryform/views/moveFocus.js